<!--
//================================================================================
// ID:-
// ProjectName: 都営住宅募集オンライン申請
// SystemName: 都営住宅募集オンラインシステム
// JavaScriptName:TextInput
//
// <<Modification History>>
// Version  | Date       | Updated By            | Content
// ---------+------------+-----------------------+--------------------------------
// 01.00.00 | 2021/08/06 | 富士通株式会社         | 新規作成
// 01.01.00 | 2024/03/29 | 富士通Japan株式会社    | 令和5年度改修対応（3次リリース）
//================================================================================
-->
<template>
	<input
		v-bind="$attrs"
		type="text"
		ref="textInput"
		:value="modelValue"
		:placeholder="placeholder == null ? '' : placeholder"
		:class="['contents__input--text', {'contents__error--item': errorFlg}, imeMode ? 'ime-active' : 'ime-inactive']"
		:disabled="disabledFlg"
		:maxlength="maxLength"
		:tabindex="tabindex"
		@input="$emit('update:modelValue', $event.target.value)"
		@keydown.delete.stop="keyControl"
		@blur="$emit('blur', $event)"
		@keydown="$emit('keydown', $event)"
	>
</template>

<script>
import { co_checkLength, co_isEmpty } from '@/common/checkUtility.js';
import { co_getMessage } from '@/common/messageUtility.js';
import { co_trim } from '@/common/stringUtility.js';

import { TEXTINPUT_TYPE } from '@/const/const.js';
import { STORE_ERROR_SET_INPUT_ERR, STORE_ERROR_IS_ERRFLG, STORE_ERROR_IS_INPUT_ERRFLG, STORE_VIEW_IS_MOBILE } from '@/const/store.js';
import { MSG_FULL_STR, MSG_HIRAGANA, MSG_HALF_STR, MSG_HALF_KANA, MSG_ALPHA_NUM, MSG_HALF_NUM } from '@/const/messageId.js';

export default {
	inheritAttrs: false,
	props: {
		modelValue: {type: String, default: ''},					// 項目値
		name: {type: String, default: ''},							// 項目名
		requireFlg: { type: Boolean, default: false },				// true : 必須
		textType: { type: Number, default: TEXTINPUT_TYPE.ALL },	// 書式
		maxLength: { type: Number, default: 0 },					// 最大文字数
		placeholder: { type: String, default: null },				// 入力補助
		disabledFlg: { type: Boolean, default: false },				// 活性・非活性フラグ
		tabindex: {type: Number, default: 0},						// タブインデックス
		groupId: {type: String, default: ''},						// 項目グループID
	},
	emits: ['update:modelValue', 'blur', 'keydown'],
	data: function () {
		return {
			errorFlg: false,
			imeMode: (this.textType == TEXTINPUT_TYPE.ALL
						|| this.textType == TEXTINPUT_TYPE.FULL
						|| this.textType == TEXTINPUT_TYPE.HIRAGANA
						|| this.textType == TEXTINPUT_TYPE.HALF_KANA) ? true : false,
		}
	},
	computed: {
		errFlg: function () {
			return this.$store.getters[STORE_ERROR_IS_ERRFLG];
		}
	},
	watch: {
		disabledFlg: function( val ) {
			if( val ) {
				this.clearError();
			}
		},
		errFlg: function(newValue) {
			if(newValue === false) {
				this.clearError();
			}
		}
	},
	methods: {
		keyControl: function () {
			// キーイベントを無効化するためのメソッドのため、処理なし
		},
		// 妥当性チェック
		validate: function () {
			// TRIM処理
			let value = co_trim(this.modelValue);
			this.$emit('update:modelValue', value);

			let error = false;
			let inputError = this.$store.getters[STORE_ERROR_IS_INPUT_ERRFLG];

			// 必須チェック
			if ( this.requireFlg === true ) {
				if ( co_isEmpty(value) ) {
					this.$store.commit(STORE_ERROR_SET_INPUT_ERR,
										{id: this.groupId, msg: co_getMessage('E0053', this.name)});
					error = true;
				}
			}

			if ( !co_isEmpty(value) ) {
				// 全角半角変換
				switch (this.textType) {
					// 全角のみ
					case TEXTINPUT_TYPE.FULL:
						// 半角英数字を全角に変換
						value = this.zenkaku2Hankaku(value);
						this.$emit('update:modelValue', value);
						break;

					// 半角カナのみ
					case TEXTINPUT_TYPE.HALF_KANA:
						// 全角カタカナを半角カタカナに変換
						value = this.zenkana2Hankana(value);
						this.$emit('update:modelValue', value);
						break;
				}

				// 最大入力文字チェック
				if ( !co_checkLength(value, this.maxLength) ) {
					this.$store.commit(STORE_ERROR_SET_INPUT_ERR,
										{id: this.groupId, msg: co_getMessage('E0055', this.name, String(this.maxLength))});
					error = true;
				}

				// 属性チェック
				switch (this.textType) {
					// 全て
					case TEXTINPUT_TYPE.ALL:
						break;

					// 全角のみ
					case TEXTINPUT_TYPE.FULL:
						// 「半角英数記号」が設定されていた場合エラー
						if ( !value.match(/^[^\\x01-\x7e\uFF61-\uFF9F !"#$%&'()*+,\-./]+$/) ) {
							this.$store.commit(STORE_ERROR_SET_INPUT_ERR,
												{id: this.groupId, msg: co_getMessage('E0056', this.name, MSG_FULL_STR)});
							error = true;
						}
						break;

					// 全角ひらがなのみ
					case TEXTINPUT_TYPE.HIRAGANA:
						// 「全角ひらがな」以外が設定されていた場合エラー
						if ( !value.match(/^[\u3041-\u309f\u3000・ー]+$/) ) {
							this.$store.commit(STORE_ERROR_SET_INPUT_ERR,
												{id: this.groupId, msg: co_getMessage('E0056', this.name, MSG_HIRAGANA)});
							error = true;
						}
						break;

					// 半角のみ
					case TEXTINPUT_TYPE.HALF:
						// 「半角」以外が設定されていた場合エラー
						if ( !this.isMatchHalf(value) ) {
							this.$store.commit(STORE_ERROR_SET_INPUT_ERR,
												{id: this.groupId, msg: co_getMessage('E0056', this.name, MSG_HALF_STR)});
							error = true;
						}
						break;
					
					// 半角カナのみ
					case TEXTINPUT_TYPE.HALF_KANA:
						// 「半角カナ」以外が設定されていた場合エラー
						if ( !this.isHanKana(value) ) {
							this.$store.commit(STORE_ERROR_SET_INPUT_ERR,
												{id: this.groupId, msg: co_getMessage('E0056', this.name, MSG_HALF_KANA)});
							error = true;
						}
						break;

					// 半角英数字
					case TEXTINPUT_TYPE.HALF_ALPHA_NUM:
						// 「半角英数」以外が設定されていた場合エラー
						if ( !value.match(/^[0-9a-zA-Z]+$/) ) {
							this.$store.commit(STORE_ERROR_SET_INPUT_ERR,
												{id: this.groupId, msg: co_getMessage('E0056', this.name, MSG_ALPHA_NUM)});
							error = true;
						}
						break;

					// 半角数字
					case TEXTINPUT_TYPE.HALF_NUM:
						// 「半角数字」以外が設定されていた場合エラー
						if ( !value.match(/^[0-9]+$/) ) {
							this.$store.commit(STORE_ERROR_SET_INPUT_ERR,
												{id: this.groupId, msg: co_getMessage('E0056', this.name, MSG_HALF_NUM)});
							error = true;
						}
						break;

					// e-mail
					case TEXTINPUT_TYPE.E_MAIL:
						// 「半角英数記号」以外が設定されていた場合エラー
						if ( !this.isMatchHalf(value) ) {
							this.$store.commit(STORE_ERROR_SET_INPUT_ERR,
												{id: this.groupId, msg: co_getMessage('E0056', this.name, MSG_HALF_STR)});
							error = true;
							break;
						}

						// 1桁目が半角英数字(小文字・大文字)でない, 1桁目が@である
						if ( !value.match(/^[a-zA-Z0-9].+$/) ) {
							this.$store.commit(STORE_ERROR_SET_INPUT_ERR,
												{id: this.groupId, msg: co_getMessage('E0004', this.name)});
							error = true;
							break;
						}

						// 「@」の文字が存在しない場合エラー
						// 「@」の前後に、それぞれ1文字以上の文字列が存在しない場合エラー
						if ( !value.match(/^[^@]+@{1}[^@]+$/) ) {
							this.$store.commit(STORE_ERROR_SET_INPUT_ERR,
												{id: this.groupId, msg: co_getMessage('E0004', this.name)});
							error = true;
							break;
						}

						// 「@」の２文字目以降に ピリオド(.) がない場合エラー
						if ( !value.match(/^.+@.+\..+$/) ) {
							this.$store.commit(STORE_ERROR_SET_INPUT_ERR,
												{id: this.groupId, msg: co_getMessage('E0004', this.name)});
							error = true;
							break;
						}

						// 形式(英数記号 @ 英数記号 . 英数記号)でないエラー
						if ( !value.match(/^[a-zA-Z0-9_+-]+(.[a-zA-Z0-9_+-]+)*@([a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9]*\.)+[a-zA-Z]{2,}$/) ) {
							this.$store.commit(STORE_ERROR_SET_INPUT_ERR,
												{id: this.groupId, msg: co_getMessage('E0004', this.name)});
							error = true;
							break;
						}

						// 半角スペースが含まれる場合エラー
						if (value.indexOf(' ') >= 0) {
							this.$store.commit(STORE_ERROR_SET_INPUT_ERR,
												{id: this.groupId, msg: co_getMessage('E0004', this.name)});
							error = true;
							break;
						}

						break;
				}
			}

			if (!inputError && error) {
				// エラーの先頭項目の場合、フォーカス設定
				this.focus();
			}

			this.errorFlg = error;
			return !this.errorFlg;
		},
		// 半角のみか
		isMatchHalf : function (str) {
			return str.match(/^[\x20-\x7e\uFF61-\uFF9F]+$/);
		},
		// 半角カナのみか
		isHanKana : function (str) {
			var reg = new RegExp(/^[ｦ-ﾟ]*$/);
			return reg.test(str);
		},
		// 半角を全角に変換
		zenkaku2Hankaku : function (str) {
			return this.hankana2Zenkana(str.replace(/[!-~]/g, function(s) {
					return String.fromCharCode(s.charCodeAt(0) + 0xFEE0);
			}));
		},
		// 全角カタカナを半角カタカナに変換
		zenkana2Hankana : function (str) {
			var kanaMap = {
				"ガ": "ｶﾞ", "ギ": "ｷﾞ", "グ": "ｸﾞ", "ゲ": "ｹﾞ", "ゴ": "ｺﾞ",
				"ザ": "ｻﾞ", "ジ": "ｼﾞ", "ズ": "ｽﾞ", "ゼ": "ｾﾞ", "ゾ": "ｿﾞ",
				"ダ": "ﾀﾞ", "ヂ": "ﾁﾞ", "ヅ": "ﾂﾞ", "デ": "ﾃﾞ", "ド": "ﾄﾞ",
				"バ": "ﾊﾞ", "ビ": "ﾋﾞ", "ブ": "ﾌﾞ", "ベ": "ﾍﾞ", "ボ": "ﾎﾞ",
				"パ": "ﾊﾟ", "ピ": "ﾋﾟ", "プ": "ﾌﾟ", "ペ": "ﾍﾟ", "ポ": "ﾎﾟ",
				"ヴ": "ｳﾞ", "ヷ": "ﾜﾞ", "ヺ": "ｦﾞ",
				"ア": "ｱ", "イ": "ｲ", "ウ": "ｳ", "エ": "ｴ", "オ": "ｵ",
				"カ": "ｶ", "キ": "ｷ", "ク": "ｸ", "ケ": "ｹ", "コ": "ｺ",
				"サ": "ｻ", "シ": "ｼ", "ス": "ｽ", "セ": "ｾ", "ソ": "ｿ",
				"タ": "ﾀ", "チ": "ﾁ", "ツ": "ﾂ", "テ": "ﾃ", "ト": "ﾄ",
				"ナ": "ﾅ", "ニ": "ﾆ", "ヌ": "ﾇ", "ネ": "ﾈ", "ノ": "ﾉ",
				"ハ": "ﾊ", "ヒ": "ﾋ", "フ": "ﾌ", "ヘ": "ﾍ", "ホ": "ﾎ",
				"マ": "ﾏ", "ミ": "ﾐ", "ム": "ﾑ", "メ": "ﾒ", "モ": "ﾓ",
				"ヤ": "ﾔ", "ユ": "ﾕ", "ヨ": "ﾖ",
				"ラ": "ﾗ", "リ": "ﾘ", "ル": "ﾙ", "レ": "ﾚ", "ロ": "ﾛ",
				"ワ": "ﾜ", "ヲ": "ｦ", "ン": "ﾝ",
				"ァ": "ｧ", "ィ": "ｨ", "ゥ": "ｩ", "ェ": "ｪ", "ォ": "ｫ",
				"ッ": "ｯ", "ャ": "ｬ", "ュ": "ｭ", "ョ": "ｮ","　": " ",
				"。": "｡", "、": "､", "ー": "ｰ", "「": "｢", "」": "｣", "・": "･"
			}
			var reg = new RegExp('(' + Object.keys(kanaMap).join('|') + ')', 'g');
			return str
					.replace(reg, function (match) {
						return kanaMap[match];
					})
					.replace(/゛/g, 'ﾞ')
					.replace(/゜/g, 'ﾟ');
		},
		// 半角カタカナを全角カタカナに変換
		hankana2Zenkana : function (str) {
			var kanaMap = {
				'ｶﾞ': 'ガ', 'ｷﾞ': 'ギ', 'ｸﾞ': 'グ', 'ｹﾞ': 'ゲ', 'ｺﾞ': 'ゴ',
				'ｻﾞ': 'ザ', 'ｼﾞ': 'ジ', 'ｽﾞ': 'ズ', 'ｾﾞ': 'ゼ', 'ｿﾞ': 'ゾ',
				'ﾀﾞ': 'ダ', 'ﾁﾞ': 'ヂ', 'ﾂﾞ': 'ヅ', 'ﾃﾞ': 'デ', 'ﾄﾞ': 'ド',
				'ﾊﾞ': 'バ', 'ﾋﾞ': 'ビ', 'ﾌﾞ': 'ブ', 'ﾍﾞ': 'ベ', 'ﾎﾞ': 'ボ',
				'ﾊﾟ': 'パ', 'ﾋﾟ': 'ピ', 'ﾌﾟ': 'プ', 'ﾍﾟ': 'ペ', 'ﾎﾟ': 'ポ',
				'ｳﾞ': 'ヴ', 'ﾜﾞ': 'ヷ', 'ｦﾞ': 'ヺ',
				'ｱ': 'ア', 'ｲ': 'イ', 'ｳ': 'ウ', 'ｴ': 'エ', 'ｵ': 'オ',
				'ｶ': 'カ', 'ｷ': 'キ', 'ｸ': 'ク', 'ｹ': 'ケ', 'ｺ': 'コ',
				'ｻ': 'サ', 'ｼ': 'シ', 'ｽ': 'ス', 'ｾ': 'セ', 'ｿ': 'ソ',
				'ﾀ': 'タ', 'ﾁ': 'チ', 'ﾂ': 'ツ', 'ﾃ': 'テ', 'ﾄ': 'ト',
				'ﾅ': 'ナ', 'ﾆ': 'ニ', 'ﾇ': 'ヌ', 'ﾈ': 'ネ', 'ﾉ': 'ノ',
				'ﾊ': 'ハ', 'ﾋ': 'ヒ', 'ﾌ': 'フ', 'ﾍ': 'ヘ', 'ﾎ': 'ホ',
				'ﾏ': 'マ', 'ﾐ': 'ミ', 'ﾑ': 'ム', 'ﾒ': 'メ', 'ﾓ': 'モ',
				'ﾔ': 'ヤ', 'ﾕ': 'ユ', 'ﾖ': 'ヨ',
				'ﾗ': 'ラ', 'ﾘ': 'リ', 'ﾙ': 'ル', 'ﾚ': 'レ', 'ﾛ': 'ロ',
				'ﾜ': 'ワ', 'ｦ': 'ヲ', 'ﾝ': 'ン',
				'ｧ': 'ァ', 'ｨ': 'ィ', 'ｩ': 'ゥ', 'ｪ': 'ェ', 'ｫ': 'ォ',
				'ｯ': 'ッ', 'ｬ': 'ャ', 'ｭ': 'ュ', 'ｮ': 'ョ',
				'｡': '。', '､': '、', 'ｰ': 'ー', '｢': '「', '｣': '」', '･': '・', ' ': '　'
			};

			var reg = new RegExp('(' + Object.keys(kanaMap).join('|') + ')', 'g');
			return str
					.replace(reg, function (match) {
						return kanaMap[match];
					})
					.replace(/ﾞ/g, '゛')
					.replace(/ﾟ/g, '゜');
		},
		// エラー設定
		setError: function() {
			this.errorFlg = true;
		},
		// エラークリア
		clearError: function() {
			this.errorFlg = false;
		},
		// フォーカス設定
		focus: function() {
			let inputError = this.$store.getters[STORE_ERROR_IS_INPUT_ERRFLG];
			let mobileFlg = this.$store.getters[STORE_VIEW_IS_MOBILE];
			if (inputError || !mobileFlg) {
				this.$refs.textInput.focus();
			}

		},
		// 強制フォーカス設定
		forcedFocus: function() {
			this.$refs.textInput.focus();
		}
	}
}
</script>

<style scoped>
</style>